import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  DatePicker,
  Select,
  Upload,
  Button,
  Row,
  Col,
  message,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { createDoctor, updateDoctor } from "../../../http/doctorAPI";
import { fetchCategories } from "../../../http/categoryAPI";
import { $host } from "../../../http";

const { TextArea } = Input;
const { Option } = Select;

const DoctorForm = ({ mode = "add", doctor = null, onSuccess }) => {
  const [categories, setCategories] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [form] = Form.useForm();

  // Load categories
  useEffect(() => {
    fetchCategories().then(setCategories).catch(console.error);
  }, []);

  // Prefill form when editing
  useEffect(() => {
    if (doctor) {
      form.setFieldsValue({
        ...doctor,
        birthDate: doctor.birthDate ? dayjs(doctor.birthDate) : null,
        categoryId: doctor.categoryId || undefined,
      });

      if (doctor.img_main) {
        setPreviewUrl($host.defaults.baseURL + `photos/doctors/${doctor.img_main}`);
      }
    }
  }, [doctor, form]);

  const beforeUpload = (file) => {
    const isImage =
      file.type === "image/jpeg" ||
      file.type === "image/png" ||
      file.type === "image/jpg";

    if (!isImage) {
      message.error("Only JPG/PNG images allowed.");
      return Upload.LIST_IGNORE;
    }

    setPreviewUrl(URL.createObjectURL(file));
    return false;
  };

  const handleImageChange = ({ fileList }) => {
    setFileList(fileList);
  };

  const handleSubmit = async (values) => {
    const formData = new FormData();

    Object.entries(values).forEach(([key, value]) => {
      if (value === undefined || value === null) return;

      if (key === "birthDate") {
        formData.append(key, value.format("YYYY-MM-DD"));
      } else {
        formData.append(key, value);
      }
    });

    if (fileList.length > 0 && fileList[0].originFileObj) {
      formData.append("img_main", fileList[0].originFileObj);
    }

    try {
      if (mode === "add") {
        await createDoctor(formData);
        message.success("Doctor created!");
      } else {
        await updateDoctor(doctor.id, formData);
        // ✅ log the new categoryId from form values
        console.log("Doctor updated, new categoryId:", values.categoryId);
        message.success("Doctor updated!");
      }

      form.resetFields();
      setFileList([]);
      setPreviewUrl(null);
      onSuccess?.();
    } catch (err) {
      console.error(err);
      message.error("Error saving doctor.");
    }
  };

  return (
    <Form
      layout="vertical"
      form={form}
      onFinish={handleSubmit}
      initialValues={{
        categoryId: doctor?.categoryId || undefined,
      }}
    >
      <h3>{mode === "add" ? "Add Doctor" : "Update Doctor"}</h3>

      {/* Names */}
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item name="firstNameAM" label="First Name (AM)" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="lastNameAM" label="Last Name (AM)" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="fatherNameAM" label="Father Name (AM)">
            <Input />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item name="firstNameEN" label="First Name (EN)" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="lastNameEN" label="Last Name (EN)" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="fatherNameEN" label="Father Name (EN)">
            <Input />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item name="firstNameRU" label="First Name (RU)" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="lastNameRU" label="Last Name (RU)" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="fatherNameRU" label="Father Name (RU)">
            <Input />
          </Form.Item>
        </Col>
      </Row>

      {/* Contacts */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="phone" label="Phone" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true },
              { type: "email", message: "Invalid email" },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="birthDate" label="Birth Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="cabinet" label="Cabinet">
            <Input />
          </Form.Item>

          {/* Department */}
          <Form.Item
            name="categoryId"
            label="Department"
            rules={[{ required: true }]}
          >
            <Select placeholder="Select Department">
              {categories.map((c) => (
                <Option key={c.id} value={c.id}>
                  {c.nameAM}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Image */}
          <Form.Item label="Image">
            <Upload
              fileList={fileList}
              beforeUpload={beforeUpload}
              onChange={handleImageChange}
              listType="picture"
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>Upload Image</Button>
            </Upload>
            {previewUrl && (
              <img
                src={previewUrl}
                alt="preview"
                style={{
                  width: 140,
                  marginTop: 10,
                  borderRadius: 8,
                  border: "1px solid #ddd",
                }}
              />
            )}
          </Form.Item>
        </Col>

        {/* Armenian medical */}
        <Col span={4}>
          <Form.Item name="positionAM" label="Position (AM)">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="scientificDegreeAM" label="Degree (AM)">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="articlesAM" label="Articles (AM)">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="membershipsAM" label="Memberships (AM)">
            <TextArea rows={2} />
          </Form.Item>
        </Col>

        {/* English medical */}
        <Col span={4}>
          <Form.Item name="positionEN" label="Position (EN)">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="scientificDegreeEN" label="Degree (EN)">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="articlesEN" label="Articles (EN)">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="membershipsEN" label="Memberships (EN)">
            <TextArea rows={2} />
          </Form.Item>
        </Col>

        {/* Russian medical */}
        <Col span={4}>
          <Form.Item name="positionRU" label="Position (RU)">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="scientificDegreeRU" label="Degree (RU)">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="articlesRU" label="Articles (RU)">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="membershipsRU" label="Memberships (RU)">
            <TextArea rows={2} />
          </Form.Item>
        </Col>
      </Row>

      {/* Experience */}
      <Row>
        <Col span={24}>
          <Form.Item name="workingExperience" label="Working Experience">
            <TextArea rows={3} />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item>
        <Button type="primary" htmlType="submit" block>
          {mode === "add" ? "Create Doctor" : "Update Doctor"}
        </Button>
      </Form.Item>
    </Form>
  );
};

export default DoctorForm;
